import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, RefreshCcw, Clock, Wrench, PhoneCall, CheckCircle2, ChevronDown } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import usePageTitle from "@/lib/usePageTitle";

const covered = [
    "Smart switches, plugs, dimmers, controllers",
    "Smart lights, LED strips & bulbs supplied by GoSmartNest",
    "Smart curtain motors & rails",
    "IR hubs & AC controllers",
    "Smart door locks & video doorbells",
    "Motion, door and gas sensors",
    "IoT control hubs installed by our team",
];

const notCovered = [
    "Damage caused by accidents, misuse or unauthorised modifications",
    "Non-GoSmartNest appliances (your existing AC, TV, fan, geyser)",
    "Consumables like batteries used in remote sensors",
    "Damage due to power surges without a stabiliser / surge protector",
];

const faqs = [
    { q: "How do I claim the warranty?", a: "Call or WhatsApp our support number. A technician is scheduled within 48 hours in your city. Most issues are resolved on the first visit." },
    { q: "Do I need to keep a bill / receipt?", a: "No. Your installation is linked to your registered phone number — that's all we need to look up your warranty and service history." },
    { q: "How can I extend the warranty?", a: "You can extend by another year within 30 days of your first warranty expiring. Our team will share the extension quote based on your installed devices." },
    { q: "What is the Lifetime Service Guarantee?", a: "Even after warranty ends, we continue to service, upgrade and re-install your GoSmartNest devices at a nominal service charge — for as long as your home stands." },
    { q: "Is labour covered under warranty?", a: "Yes. During the warranty period, all labour for repair or replacement of covered devices is free of charge." },
    { q: "What if I move house?", a: "Reach out to us — for a small relocation fee we'll de-install and re-install your smart devices in your new home." },
];

export default function Warranty() {
    usePageTitle("Warranty & Service");
    return (
        <div className="bg-white" data-testid="warranty-page">
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
                <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-radial-cyan pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-16 lg:pt-20 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="text-[11px] tracking-[0.2em] uppercase text-[#1BB6F0] font-semibold">Warranty & Service</div>
                        <motion.h1
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0B1440] leading-[1.05]"
                            data-testid="warranty-title"
                        >
                            A smart home you can<br />
                            <span className="text-brand-gradient">trust for a lifetime.</span>
                        </motion.h1>
                        <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                            Every GoSmartNest installation ships with a 1-year no-questions-asked warranty,
                            an extendable second year, and a lifetime service guarantee on all our devices.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href="tel:+918144814162" data-testid="warranty-cta-call" className="inline-flex items-center gap-2 bg-[#1B2E7A] hover:bg-[#13215A] text-white rounded-full px-7 py-3.5 font-medium transition-colors">
                                <PhoneCall className="w-4 h-4" /> Call support
                            </a>
                            <Link to="/contact" data-testid="warranty-cta-contact" className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-[#1BB6F0] text-[#1B2E7A] rounded-full px-7 py-3.5 font-medium transition-colors">
                                Request a service visit
                            </Link>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="rounded-[28px] bg-[#0B1440] text-white p-7 border border-white/10 shadow-xl shadow-[#1B2E7A]/20">
                            <div className="text-[11px] tracking-[0.2em] uppercase text-[#1BB6F0] font-semibold">Included in every install</div>
                            <div className="mt-6 space-y-4">
                                {[
                                    { icon: ShieldCheck, t: "1-Year Warranty", d: "Included on every installed device." },
                                    { icon: RefreshCcw, t: "Extendable", d: "Add a second year within 30 days of first-year expiry." },
                                    { icon: Clock, t: "48-hour service", d: "Technician scheduled within 48 hours of your call." },
                                    { icon: Wrench, t: "Lifetime service", d: "We keep your home smart for as long as it stands." },
                                ].map((r, i) => (
                                    <div key={i} className="flex items-start gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-[#1BB6F0]/15 text-[#1BB6F0] flex items-center justify-center shrink-0"><r.icon className="w-4 h-4" /></div>
                                        <div>
                                            <div className="font-semibold">{r.t}</div>
                                            <div className="text-sm text-white/70 mt-0.5">{r.d}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Coverage split */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div className="rounded-3xl border border-slate-200 bg-white p-8" data-testid="warranty-covered">
                        <div className="w-11 h-11 rounded-2xl bg-[#E8F8FE] text-[#1BB6F0] flex items-center justify-center"><CheckCircle2 className="w-5 h-5" /></div>
                        <h3 className="mt-5 text-2xl font-semibold text-[#1B2E7A]">What's covered</h3>
                        <ul className="mt-5 space-y-3">
                            {covered.map((c, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#1BB6F0] text-white flex items-center justify-center shrink-0">
                                        <CheckCircle2 className="w-3 h-3" />
                                    </span>
                                    {c}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-8" data-testid="warranty-not-covered">
                        <div className="w-11 h-11 rounded-2xl bg-white text-slate-500 flex items-center justify-center border border-slate-200"><ChevronDown className="w-5 h-5 rotate-45" /></div>
                        <h3 className="mt-5 text-2xl font-semibold text-[#1B2E7A]">What isn't</h3>
                        <ul className="mt-5 space-y-3">
                            {notCovered.map((c, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                                    <span className="mt-0.5 w-5 h-5 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center shrink-0 text-[10px] font-bold">×</span>
                                    {c}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Claim process */}
            <section className="bg-[#0B1440] text-white py-24">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <div className="max-w-2xl">
                        <div className="text-[11px] tracking-[0.2em] uppercase text-[#1BB6F0] font-semibold">How to claim</div>
                        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
                            Warranty in 3 easy steps.
                        </h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[
                            { n: "01", t: "Call or WhatsApp", d: "Reach us on our support line — no forms, no forwarding." },
                            { n: "02", t: "Technician scheduled", d: "A GoSmartNest engineer is booked within 48 hours in your city." },
                            { n: "03", t: "Fixed or replaced", d: "Most issues resolved on the first visit. Zero labour charge." },
                        ].map((s, i) => (
                            <div key={i} className="p-7 rounded-3xl bg-white/[0.04] border border-white/10 hover:border-[#1BB6F0]/60 transition-colors" data-testid={`claim-step-${i}`}>
                                <div className="font-mono text-xs text-[#1BB6F0] tracking-widest">{s.n}</div>
                                <div className="mt-4 text-xl font-semibold">{s.t}</div>
                                <div className="mt-2 text-sm text-white/70 leading-relaxed">{s.d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="max-w-4xl mx-auto px-5 sm:px-8 py-24" data-testid="warranty-faqs">
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#1BB6F0] font-semibold">Warranty FAQ</div>
                <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-[#1B2E7A]">Common questions</h2>
                <Accordion type="single" collapsible className="mt-8 space-y-3">
                    {faqs.map((f, i) => (
                        <AccordionItem key={i} value={`w-${i}`} className="border border-slate-200 rounded-2xl bg-white px-5" data-testid={`faq-${i}`}>
                            <AccordionTrigger className="text-left text-[#1B2E7A] hover:no-underline font-medium py-5">{f.q}</AccordionTrigger>
                            <AccordionContent className="text-slate-600 pb-5 leading-relaxed">{f.a}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </section>
        </div>
    );
}
